// Shared theming for onboarding "structure" division cards (CSP-safe dynamic CSS).
// Used by the structure step grid and the onboarding ending recap strip.

import { createDynamicCSS } from '../../../../../utils/CreateDynamicCSS';

/**
 * Cache module-scope des classes CSS générées dynamiquement pour les fonds
 * de card du step "structure". `createDynamicCSS` n'a pas de cache interne :
 * sans ce Set, chaque render réinjecterait la même règle dans le <style>
 * partagé du document. La clé est la classe elle-même (déterministe à partir
 * des deux couleurs), donc deux divisions avec la même paire de couleurs
 * partageront naturellement une seule règle.
 */
const __STRUCTURE_CARD_BG_CACHE = new Set();
/** Dashboard strip selectors added after ending-only cache (same color pair). */
const __STRUCTURE_CARD_BG_DASHBOARD_CACHE = new Set();
/** Roster file summary strip (compact cards, separate dimensions). */
const __STRUCTURE_CARD_BG_ROSTER_CACHE = new Set();
/** Roster org-positions browse modal cards. */
const __STRUCTURE_CARD_BG_ORG_POS_BROWSE_CACHE = new Set();

function parseHexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function lightenRgb({ r, g, b }, ratio) {
  return {
    r: Math.round(r + (255 - r) * ratio),
    g: Math.round(g + (255 - g) * ratio),
    b: Math.round(b + (255 - b) * ratio),
  };
}

function rgbToCss({ r, g, b }, alpha = 1) {
  return alpha >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Ratio d'éclaircissement pour les cards du niveau "section" (aligné sur le step structure). */
export const SECTION_BG_LIGHTEN_RATIO = 0.1;

export function getStructureAccentColors(color1, color2, lightenRatio = 0) {
  const s1 = color1 != null && color1 !== '' ? String(color1).trim() : '';
  const s2 = color2 != null && color2 !== '' ? String(color2).trim() : '';
  /* Une seule couleur en DB : on duplique pour le dégradé (même rendu que beaucoup de cards admin). */
  const raw1 = s1 || s2;
  const raw2 = s2 || s1;
  if (!raw1 || !raw2) return null;
  let c1 = raw1;
  let c2 = raw2;
  let c1Rgb = parseHexToRgb(raw1);
  let c2Rgb = parseHexToRgb(raw2);
  if (lightenRatio > 0) {
    if (c1Rgb) {
      c1Rgb = lightenRgb(c1Rgb, lightenRatio);
      c1 = rgbToCss(c1Rgb);
    }
    if (c2Rgb) {
      c2Rgb = lightenRgb(c2Rgb, lightenRatio);
      c2 = rgbToCss(c2Rgb);
    }
  }
  const accent = c1Rgb ? rgbToCss(lightenRgb(c1Rgb, 0.28)) : c1;
  const glow = c1Rgb
    ? rgbToCss(lightenRgb(c1Rgb, 0.12), 0.42)
    : 'rgba(255, 255, 255, 0.18)';
  const glowHover = c1Rgb
    ? rgbToCss(lightenRgb(c1Rgb, 0.22), 0.62)
    : 'rgba(255, 255, 255, 0.3)';
  return { c1, c2, accent, glow, glowHover };
}

const __MO_SAFE_CLASS = (s) => s.replace(/[^A-Za-z0-9_-]/g, '');

function lightenSuffix(lightenRatio) {
  return lightenRatio > 0 ? `-l${Math.round(lightenRatio * 100)}` : '';
}

export function getStructureCardBgClass(color1, color2, lightenRatio = 0) {
  const colors = getStructureAccentColors(color1, color2, lightenRatio);
  if (!colors) return null;
  const { c1, c2, accent, glow, glowHover } = colors;
  const className =
    `mo-structure-card-bg-${__MO_SAFE_CLASS(color1)}-${__MO_SAFE_CLASS(color2)}` +
    lightenSuffix(lightenRatio);

  const surfaceBg = {
    background: `linear-gradient(180deg, ${c1} 0%, ${c2} 100%)`,
    'border-color': accent,
    'box-shadow': `0 0 0 1px ${accent}, 0 6px 22px ${glow}`,
  };
  const endingSurfaceSelector = `.members-onboarding-ending-structure__cardSurface.${className}`;
  const dashboardSurfaceSelector =
    `.member-dashboard__structure-recap__card-surface.${className}`;
  const rosterSurfaceSelector = `.roster-file-structure-strip__surface.${className}`;
  const rosterSurfaceBg = {
    background: surfaceBg.background,
    'border-color': accent,
    'box-shadow': `0 0 0 1px ${accent}, 0 2px 8px ${glow}`,
  };
  const orgPosBrowseSelector = `.org-positions-browse-card.${className}`;
  const orgPosBrowseBg = {
    'border-color': accent,
    'box-shadow': `inset 0 -2px 0 ${accent}, 0 4px 18px ${glow}`,
    background: `linear-gradient(165deg, ${c1} 0%, ${c2} 100%)`,
  };
  const hoverShadow = {
    'box-shadow': `0 0 0 1px ${accent}, 0 10px 20px ${glowHover}`,
  };
  const orgPosBrowseHover = {
    'box-shadow': `inset 0 -2px 0 ${accent}, 0 8px 22px ${glowHover}`,
  };

  if (!__STRUCTURE_CARD_BG_CACHE.has(className)) {
    createDynamicCSS(className, surfaceBg);
    createDynamicCSS(endingSurfaceSelector, surfaceBg);
    createDynamicCSS(dashboardSurfaceSelector, surfaceBg);
    createDynamicCSS(rosterSurfaceSelector, rosterSurfaceBg);
    createDynamicCSS(orgPosBrowseSelector, orgPosBrowseBg);
    createDynamicCSS(`.${className}:hover, .${className}:focus-visible`, hoverShadow);
    createDynamicCSS(
      `${endingSurfaceSelector}:hover, ${endingSurfaceSelector}:focus-visible`,
      hoverShadow,
    );
    createDynamicCSS(
      `${dashboardSurfaceSelector}:hover, ${dashboardSurfaceSelector}:focus-visible`,
      hoverShadow,
    );
    createDynamicCSS(
      `${orgPosBrowseSelector}:hover, ${orgPosBrowseSelector}:focus-visible`,
      orgPosBrowseHover,
    );
    createDynamicCSS(`.${className} .members-onboarding-structure__card-cta`, {
      'border-color': accent,
      color: accent,
    });
    __STRUCTURE_CARD_BG_CACHE.add(className);
    __STRUCTURE_CARD_BG_DASHBOARD_CACHE.add(className);
    __STRUCTURE_CARD_BG_ROSTER_CACHE.add(className);
    __STRUCTURE_CARD_BG_ORG_POS_BROWSE_CACHE.add(className);
    return className;
  }

  if (!__STRUCTURE_CARD_BG_DASHBOARD_CACHE.has(className)) {
    createDynamicCSS(dashboardSurfaceSelector, surfaceBg);
    createDynamicCSS(
      `${dashboardSurfaceSelector}:hover, ${dashboardSurfaceSelector}:focus-visible`,
      hoverShadow,
    );
    __STRUCTURE_CARD_BG_DASHBOARD_CACHE.add(className);
  }

  if (!__STRUCTURE_CARD_BG_ROSTER_CACHE.has(className)) {
    createDynamicCSS(rosterSurfaceSelector, rosterSurfaceBg);
    __STRUCTURE_CARD_BG_ROSTER_CACHE.add(className);
  }

  if (!__STRUCTURE_CARD_BG_ORG_POS_BROWSE_CACHE.has(className)) {
    createDynamicCSS(orgPosBrowseSelector, orgPosBrowseBg);
    createDynamicCSS(
      `${orgPosBrowseSelector}:hover, ${orgPosBrowseSelector}:focus-visible`,
      orgPosBrowseHover,
    );
    __STRUCTURE_CARD_BG_ORG_POS_BROWSE_CACHE.add(className);
  }

  return className;
}

export function getStructureBreadcrumbBgClass(color1, color2, lightenRatio = 0) {
  const colors = getStructureAccentColors(color1, color2, lightenRatio);
  if (!colors) return null;
  const { c1, c2, accent, glow } = colors;
  const className =
    `mo-structure-bc-bg-${__MO_SAFE_CLASS(color1)}-${__MO_SAFE_CLASS(color2)}` +
    lightenSuffix(lightenRatio);
  if (__STRUCTURE_CARD_BG_CACHE.has(className)) return className;

  createDynamicCSS(className, {
    background: `linear-gradient(180deg, ${c1} 0%, ${c2} 100%)`,
    'border-color': accent,
    'box-shadow': `0 0 0 1px ${accent}`,
  });
  createDynamicCSS(`.${className}:hover, .${className}:focus-visible`, {
    'box-shadow': `0 0 0 1px ${accent}, 0 4px 14px ${glow}`,
  });
  __STRUCTURE_CARD_BG_CACHE.add(className);
  return className;
}
